/****************************************************************************
 * Copyright 2021 EPAM Systems
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 ***************************************************************************/

import { Struct, Vec2 } from 'domain/entities'
import { arrowToKet, plusToKet } from './toKet/rxnToKet'

import { Serializer } from '../serializers.types'
import { headerToKet } from './toKet/headerToKet'
import { moleculeToKet } from './toKet/moleculeToKet'
import { moleculeToStruct } from './fromKet/moleculeToStruct'
import { prepareStructForKet } from './toKet/prepare'
import { rgroupToKet } from './toKet/rgroupToKet'
import { rgroupToStruct } from './fromKet/rgroupToStruct'
import { rxnToStruct } from './fromKet/rxnToStruct'
import { simpleObjectToKet } from './toKet/simpleObjectToKet'
import { simpleObjectToStruct } from './fromKet/simpleObjectToStruct'
import { textToKet } from './toKet/textToKet'
import { textToStruct } from './fromKet/textToStruct'
import { validate } from './validate'

function parseNode(node: any, struct: any) {
  const type = node.type
  switch (type) {
    case 'arrow':
      rxnToStruct(node, struct)
      break
    case 'plus':
      rxnToStruct(node, struct)
      break
    case 'simpleObject':
      simpleObjectToStruct(node, struct)
      break
    case 'molecule':
      const currentStruct = moleculeToStruct(node)
      if (node.stereoFlagPosition) {
        const fragment = currentStruct.frags.get(0)!
        fragment.stereoFlagPosition = new Vec2(node.stereoFlagPosition)
      }

      currentStruct.mergeInto(struct)
      break
    case 'rgroup':
      rgroupToStruct(node).mergeInto(struct)
      break
    case 'text':
      textToStruct(node, struct)
      break
    default:
      break
  }
}
export class KetSerializer implements Serializer<Struct> {
  deserialize(content: string): Struct {
    const resultingStruct = new Struct()
    const ket = JSON.parse(content)
    if (!validate(ket)) {
      throw new Error('Cannot deserialize input JSON.')
    }
    resultingStruct.name = ket.header ? ket.header.moleculeName : null
    const nodes = ket.root.nodes
    Object.keys(nodes).forEach(i => {
      if (nodes[i].type) parseNode(nodes[i], resultingStruct)
      else if (nodes[i].$ref) parseNode(ket[nodes[i].$ref], resultingStruct)
    })

    return resultingStruct
  }

  serialize(struct: Struct): string {
    const result: any = {
      root: {
        nodes: []
      }
    }

    const header = headerToKet(struct)
    if (header) result.header = header

    const ketNodes = prepareStructForKet(struct)

    let moleculeId = 0
    ketNodes.forEach(item => {
      switch (item.type) {
        case 'molecule': {
          result.root.nodes.push({ $ref: `mol${moleculeId}` })
          result[`mol${moleculeId++}`] = moleculeToKet(item.fragment)
          break
        }
        case 'rgroup': {
          result.root.nodes.push({ $ref: `rg${item.data!.rgnumber}` })
          result[`rg${item.data!.rgnumber}`] = rgroupToKet(
            item.fragment,
            item.data
          )
          break
        }
        case 'plus': {
          result.root.nodes.push(plusToKet(item))
          break
        }
        case 'arrow': {
          result.root.nodes.push(arrowToKet(item))
          break
        }
        case 'simpleObject': {
          result.root.nodes.push(simpleObjectToKet(item))
          break
        }
        case 'text': {
          result.root.nodes.push(textToKet(item))
          break
        }
        default:
          break
      }
    })

    return JSON.stringify(result, null, 4)
  }
}
